<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/theme'
import { useToastStore } from '@/stores/toast'
import { useNotificationStore } from '@/stores/notifications'
import NotificationBell from './NotificationBell.vue'
import ConnectionIndicator from './ConnectionIndicator.vue'
import AppAvatar from '@/components/ui/AppAvatar.vue'

const emit = defineEmits<{ 'toggle-sidebar': [] }>()

const auth = useAuthStore()
const theme = useThemeStore()
const toast = useToastStore()
const notifications = useNotificationStore()
const route = useRoute()
const router = useRouter()

const menuOpen = ref(false)
const menu = ref<HTMLElement | null>(null)

const title = computed(() => (typeof route.meta.title === 'string' ? route.meta.title : 'Přehled'))

async function logout(): Promise<void> {
  menuOpen.value = false
  await auth.logout()
  notifications.reset()
  toast.info('Byli jste odhlášeni.')
  await router.push({ name: 'login' })
}

function onClickOutside(event: MouseEvent): void {
  if (menuOpen.value && menu.value && !menu.value.contains(event.target as Node)) {
    menuOpen.value = false
  }
}

onMounted(() => document.addEventListener('mousedown', onClickOutside))
onBeforeUnmount(() => document.removeEventListener('mousedown', onClickOutside))
</script>

<template>
  <header class="topbar">
    <button
      type="button"
      class="topbar__burger"
      aria-label="Otevřít navigaci"
      @click="emit('toggle-sidebar')"
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M4 7h16M4 12h16M4 17h16"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
        />
      </svg>
    </button>

    <h1 class="topbar__title">
      {{ title }}
    </h1>

    <div class="topbar__actions">
      <ConnectionIndicator />

      <button
        type="button"
        class="topbar__icon"
        :aria-label="theme.theme === 'dark' ? 'Přepnout na světlý režim' : 'Přepnout na tmavý režim'"
        @click="theme.toggle()"
      >
        <svg
          v-if="theme.theme === 'dark'"
          width="17"
          height="17"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle
            cx="12"
            cy="12"
            r="4.2"
            stroke="currentColor"
            stroke-width="1.7"
          />
          <path
            d="M12 2v2m0 16v2M2 12h2m16 0h2M4.9 4.9l1.4 1.4m11.4 11.4l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
          />
        </svg>
        <svg
          v-else
          width="17"
          height="17"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M20 14.5A8.5 8.5 0 019.5 4a8.5 8.5 0 1010.5 10.5z"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linejoin="round"
          />
        </svg>
      </button>

      <NotificationBell />

      <div
        ref="menu"
        class="topbar__user"
      >
        <button
          type="button"
          class="topbar__avatar"
          :aria-expanded="menuOpen"
          aria-haspopup="true"
          @click="menuOpen = !menuOpen"
        >
          <AppAvatar
            :name="auth.user?.fullname || auth.user?.email || '?'"
            size="md"
            tone="accent"
          />
          <span class="sr-only">Uživatelské menu</span>
        </button>

        <Transition name="pop">
          <div
            v-if="menuOpen"
            class="topbar__menu"
          >
            <div class="topbar__identity">
              <strong class="truncate">{{ auth.user?.fullname }}</strong>
              <span class="muted truncate">{{ auth.user?.email }}</span>
            </div>
            <RouterLink
              to="/profile"
              class="topbar__menu-item"
              @click="menuOpen = false"
            >
              Můj profil
            </RouterLink>
            <button
              type="button"
              class="topbar__menu-item topbar__menu-item--danger"
              @click="logout"
            >
              Odhlásit se
            </button>
          </div>
        </Transition>
      </div>
    </div>
  </header>
</template>

<style scoped>
.topbar {
  position: sticky;
  top: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  gap: var(--space-3);
  height: var(--topbar-height);
  padding: 0 var(--space-5);
  background: color-mix(in srgb, var(--surface-page) 88%, transparent);
  backdrop-filter: blur(8px);
  border-bottom: 1px solid var(--border-subtle);
}

.topbar__title { flex: 1; font-size: 17px; }

.topbar__burger {
  display: none;
  place-items: center;
  width: 34px;
  height: 34px;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  background: var(--surface-card);
  color: var(--text-muted);
  cursor: pointer;
}

.topbar__actions { display: flex; align-items: center; gap: var(--space-2); }

.topbar__icon {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  background: var(--surface-card);
  color: var(--text-muted);
  cursor: pointer;
  transition: background var(--transition), color var(--transition);
}

.topbar__icon:hover { background: var(--surface-hover); color: var(--text-strong); }

.topbar__user { position: relative; }

.topbar__avatar {
  display: grid;
  place-items: center;
  padding: 0;
  border: none;
  border-radius: var(--radius-full);
  background: none;
  cursor: pointer;
}

.topbar__menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 220px;
  padding: 5px;
  background: var(--surface-raised);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  z-index: 50;
}

.topbar__identity {
  display: flex;
  flex-direction: column;
  padding: 8px 10px;
  margin-bottom: 4px;
  border-bottom: 1px solid var(--border-subtle);
  font-size: 13px;
}

.topbar__menu-item {
  display: block;
  width: 100%;
  padding: 7px 10px;
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--text-default);
  font-size: 13.5px;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
}

.topbar__menu-item:hover { background: var(--surface-hover); text-decoration: none; }
.topbar__menu-item--danger { color: var(--danger); }

.pop-enter-active, .pop-leave-active { transition: opacity var(--transition), transform var(--transition); }
.pop-enter-from, .pop-leave-to { opacity: 0; transform: translateY(-4px); }

@media (max-width: 900px) {
  .topbar__burger { display: grid; }
  .topbar { padding: 0 var(--space-4); }
}
</style>
